import React, { Fragment } from "react";
import style from "./ConfigForm.module.scss";
import { getControl } from "./fieldRegistry";
import { resolveVisibleWhen } from "./useConfigFormState";

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const renderControl = (field, ctx) => {
  const entry = getControl(field.control);
  if (!entry) return null;
  const { Component, mapProps } = entry;
  return <Component key={field.key} {...mapProps({ field, ...ctx })} />;
};

const renderFieldRow = (field, ctx, classNames) => (
  <div className={cx(style.form_field, classNames.field)} key={field.key}>
    {field.label && (
      <label className={style.form_field_label} htmlFor={field.key}>
        {field.label}:{field.required && <span className={style.form_astrick}>&#42;</span>}
      </label>
    )}
    <div className={style.form_field_control}>{renderControl(field, ctx)}</div>
  </div>
);

const buildRenderItems = (visibleFields) => {
  const items = [];
  const renderedGroups = new Set();
  let i = 0;

  while (i < visibleFields.length) {
    const field = visibleFields[i];

    if (field.group) {
      if (!renderedGroups.has(field.group)) {
        renderedGroups.add(field.group);
        items.push({ type: "group", key: field.group, fields: visibleFields.filter((entry) => entry.group === field.group) });
      }
      i += 1;
      continue;
    }

    if (field.visibleWhen) {
      const run = [field];
      let j = i + 1;
      while (j < visibleFields.length && visibleFields[j].visibleWhen === field.visibleWhen && !visibleFields[j].group) {
        run.push(visibleFields[j]);
        j += 1;
      }
      items.push({ type: "revealed", key: field.key, fields: run });
      i = j;
      continue;
    }

    items.push({ type: "single", key: field.key, field });
    i += 1;
  }

  return items;
};

/**
 * ConfigForm — edit-mode renderer for the shared ConfigForm engine.
 *
 * This component owns no state of its own — all values, change handlers, and RHF wiring are
 * supplied by the page's own hook (e.g. `useNe1`), keeping the engine reusable across pages.
 */
export const ConfigForm = ({
  sections,
  column,
  form,
  errors,
  submitErrors,
  onFieldChange,
  onMultiChange,
  register,
  customValidators,
  disabled,
  classNames = {},
}) => {
  const ctx = { form: form || {}, errors, submitErrors, onFieldChange, onMultiChange, register, customValidators, disabled };
  const visibleSections = (sections || []).filter((section) => !column || section.column === column);

  return (
    <>
      {visibleSections.map((section) => {
        const visibleFields = (section.fields || []).filter((field) => resolveVisibleWhen(field.visibleWhen, form));
        if (!visibleFields.length) return null;

        const renderItems = buildRenderItems(visibleFields);

        return (
          <section className={cx(style.form_section, classNames.section)} key={section.key}>
            <div className={style.form_section_header}>
              {section.number !== undefined && <span className={style.form_section_number}>{section.number}</span>}
              <span className={style.form_section_title}>{section.title}</span>
            </div>
            <div className={style.form_section_body}>
              {renderItems.map((item) => {
                if (item.type === "group") {
                  const groupFields = item.fields;
                  const primary = groupFields.find((entry) => entry.label) || groupFields[0];
                  const revealed = groupFields.some((entry) => entry.visibleWhen);

                  return (
                    <div className={cx(style.form_field, revealed && style.form_field_revealed, classNames.field)} key={item.key}>
                      {primary.label && (
                        <label className={style.form_field_label}>
                          {primary.label}:{primary.required && <span className={style.form_astrick}>&#42;</span>}
                        </label>
                      )}
                      <div className={cx(style.form_field_control, style.form_field_group)}>
                        {groupFields.map((groupField) => renderControl(groupField, ctx))}
                      </div>
                    </div>
                  );
                }

                if (item.type === "revealed") {
                  const isBannerAnchor = section.banner && item.fields.some((entry) => entry.errorFlagKey === section.banner.errorFlagKey);
                  const showInlineBanner =
                    isBannerAnchor && submitErrors?.[section.banner.errorFlagKey] && resolveVisibleWhen(section.banner.when, form);

                  return (
                    <Fragment key={item.key}>
                      <div className={style.form_field_revealed}>{item.fields.map((field) => renderFieldRow(field, ctx, classNames))}</div>
                      {showInlineBanner && <span className={style.form_error_message}>{section.banner.message}</span>}
                    </Fragment>
                  );
                }

                return renderFieldRow(item.field, ctx, classNames);
              })}
            </div>
          </section>
        );
      })}
    </>
  );
};
